'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Script from 'next/script'
import Button from '@/components/ui/Button'
import { TURNSTILE_SCRIPT, useTurnstile } from '@/components/tools/runs/useTurnstile'
import { KEYWORD_MAX } from '@/lib/onpage/types'

type CreateResponse =
  | { ok: true; id: string; status: string; cached: boolean }
  | { ok: false; error: string; message?: string; retryAfterSeconds?: number }

const FIELD =
  'mt-2 w-full rounded-md border bg-bg px-3 py-2.5 text-fg outline-none transition-colors focus:border-accent/60'

/** URL plus an optional target keyword. The Turnstile token travels with the create request. */
export default function CheckForm() {
  const router = useRouter()
  const [url, setUrl] = useState('')
  const [keyword, setKeyword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const turnstile = useTurnstile()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (pending) return
    setError(null)
    if (!url.trim()) {
      setError('Enter the page you want checked.')
      return
    }
    if (turnstile.enabled && !turnstile.token) {
      setError('The anti-bot check is still running. Give it a second and try again.')
      return
    }
    setPending(true)
    try {
      const res = await fetch('/api/tools/runs/onpage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          keyword: keyword.trim() || undefined,
          turnstileToken: turnstile.token ?? undefined,
        }),
      })
      const data = (await res.json()) as CreateResponse
      if (!data.ok) {
        setError(data.message ?? 'We could not start the check. Try again.')
        if (data.error === 'captcha_failed') turnstile.reset()
        setPending(false)
        return
      }
      router.push(`/tools/on-page-seo-checker/report/${data.id}`)
    } catch {
      setError('We could not reach the service. Check your connection and try again.')
      setPending(false)
    }
  }

  return (
    <form onSubmit={submit} className="card mt-10 p-6 sm:p-8" noValidate>
      {turnstile.enabled && (
        <Script src={TURNSTILE_SCRIPT} strategy="afterInteractive" onLoad={turnstile.render} />
      )}
      <div className="grid gap-5 sm:grid-cols-[3fr_2fr]">
        <label className="block" htmlFor="check-url">
          <span className="text-sm font-medium text-fg">Page URL</span>
          <input
            id="check-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/service-page"
            autoComplete="url"
            spellCheck={false}
            aria-invalid={error ? true : undefined}
            aria-describedby="check-url-hint"
            className={`${FIELD} ${error ? 'border-danger/50' : 'border-line'}`}
          />
          <span id="check-url-hint" className="mt-1 block text-xs text-muted">
            Any public page. The check takes about 30 seconds.
          </span>
        </label>
        <label className="block" htmlFor="check-keyword">
          <span className="text-sm font-medium text-fg">
            Target keyword <span className="font-normal text-muted">(optional)</span>
          </span>
          <input
            id="check-keyword"
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. marketing automation agency"
            maxLength={KEYWORD_MAX}
            autoComplete="off"
            aria-describedby="check-keyword-hint"
            className={`${FIELD} border-line`}
          />
          <span id="check-keyword-hint" className="mt-1 block text-xs text-muted">
            The phrase this page should rank for. Adds five placement checks.
          </span>
        </label>
      </div>
      <div ref={turnstile.widgetRef} className="mt-4" />
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={pending || !turnstile.ready}>
          {pending ? 'Starting the check' : 'Check the page'}
        </Button>
        <span className="text-sm text-muted">Free. No email required.</span>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-muted">
        What happens: we fetch the page once, read its title, description, canonical, social tags
        and every heading in order, run a fixed set of checks, then write rewrites and an intent
        read from what we found.
      </p>
    </form>
  )
}
